"use client";

import { Box, Typography } from "@mui/material";
import {
  ConversationRole,
  useCreateCurrentUserTermsOfServiceMutation,
  useSendMessageForSignUpDialogMutation,
  useStartLeadConvertedConversationsMutation,
  useUpdateUserProfileMutation,
} from "@yardzen/components-gql";
import { LoginForm, SubmissionError } from "@yardzen/components/login-form";
import { YzModal } from "@yardzen/components/yz-modal";
import {
  SegmentFlows,
  SegmentForms,
  SegmentSubFlows,
  clearTokenCache,
} from "@yardzen/next-client-util";
import { analytics } from "@yardzen/next-segment";
import { signIn } from "next-auth/react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useState } from "react";
import { loginModalSubtitle, loginModalTitle } from "./classes";
import {
  SignUpAndSendFirstMessageFormValues,
  SignUpAndSendMessageForm,
} from "./SignUpAndSendMessageForm";

export interface MessageFor {
  name: string;
  userProfileId?: string;
  contractorId?: string;
  role: ConversationRole;
}

interface LogInDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  segmentFormName: SegmentForms;
  callbackUrl: string;
  startConversations?: boolean;
  showSignUpAndSendMessage?: boolean;
  messageFor?: MessageFor;
  messageBody?: string;
  onSuccess?: () => void;
}

export function SignUpDialog({
  open,
  onClose,
  title,
  subtitle,
  segmentFormName,
  callbackUrl,
  startConversations = true,
  messageFor,
  showSignUpAndSendMessage,
  messageBody,
  onSuccess,
}: LogInDialogProps) {
  const [signupErrors, setSignupErrors] = useState<SubmissionError[]>([]);
  const [tosChecked, setTosChecked] = useState<boolean>(false);
  const [signUpAndSendMessageFormValues, setSignUpAndSendMessageFormValues] =
    useState<SignUpAndSendFirstMessageFormValues>({
      email: "",
      password: "",
      firstName: "",
      lastName: "",
      addressFull: "",
      tosChecked: false,
      messageToPro: messageBody ?? "",
      addressLine1: "",
      addressLine2: undefined,
      city: "",
      state: "",
      zipcode: "",
      latitude: "",
      longitude: "",
    });
  const [createTermsOfService] = useCreateCurrentUserTermsOfServiceMutation();
  const [updateUserProfile, { error: updateUserProfileError }] =
    useUpdateUserProfileMutation();
  const [startLeadConvertedConversations] =
    useStartLeadConvertedConversationsMutation();
  const [sendMessage, { error: sendMessageError }] =
    useSendMessageForSignUpDialogMutation();
  const searchParams = useSearchParams();
  const router = useRouter();

  async function sendAnalytics({
    email,
    utmSource,
    segmentFormName,
    userId,
  }: {
    email: string;
    utmSource?: string;
    segmentFormName: SegmentForms;
    userId: string;
  }) {
    const eventData = {
      referrer: typeof document !== "undefined" ? document.referrer : "",
      utm_source: utmSource,
    };
    await analytics.typedIdentify({
      userId,
      email,
    });
    await analytics.trackFormSubmitted({
      flow_name: "create_account",
      form_name: segmentFormName,
      sub_flow_name: SegmentSubFlows.LEAD_CAPTURE,
    });
    await analytics.trackAccountCreated(eventData);
    await analytics.trackFlowCompleted({ flow_name: "create_account" });
  }

  async function onSubmit(
    values: { email: string; password: string },
    customSegmentFormName?: SegmentForms,
    redirect = true,
  ) {
    setSignupErrors([]);

    const createValues = { ...values, startConversations };
    let newUserId = "";

    await fetch("/api/create-account", {
      method: "POST",
      body: JSON.stringify(createValues),
    }).then(async (res) => {
      if (res.ok) {
        const { userId } = await res.json();
        sendAnalytics({
          email: values.email,
          utmSource: searchParams.get("utm_source") ?? "",
          segmentFormName: customSegmentFormName ?? segmentFormName,
          userId,
        });

        await signIn("sign-in-with-email", {
          redirect: false,
          email: values.email,
          password: values.password,
        }).then(async () => {
          clearTokenCache();

          if (tosChecked) {
            await createTermsOfService({
              variables: {
                input: {
                  userId: userId,
                  tosAgreed: tosChecked,
                  dateOfAgree: new Date().toISOString(),
                },
              },
            });
          }
          if (redirect) {
            onClose();
            router.push(callbackUrl || "/");
          }
        });
        onSuccess && onSuccess();
        newUserId = userId;
      } else {
        const data = await res.json();
        const errors = data.errors.map(
          (e: { message: string; domain: string; reason: string }) => {
            if (e.message === "EMAIL_EXISTS") {
              return {
                field: "email",
                message: "Looks like that email already exists",
                action: "login",
              };
            }
          },
          [],
        ) as SubmissionError[];
        setSignupErrors(errors);
      }
    });
    return newUserId;
  }

  async function saveUserProfile(formValues: any, userId: string) {
    const profileData = {
      userId: userId,
      email: formValues.email,
      firstName: formValues.firstName,
      lastName: formValues.lastName,
      newMailingAddress: {
        addressLine1: formValues.addressLine1,
        addressLine2:
          formValues.addressLine2 !== "" ? formValues.addressLine2 : null,
        city: formValues.city,
        state: formValues.state,
        zipcode: formValues.zipcode,
        longitude: parseFloat(formValues.longitude),
        latitude: parseFloat(formValues.latitude),
      },
    };

    await updateUserProfile({
      variables: {
        input: { ...profileData },
      },
    });
  }

  async function sendMessageTo(message: string, fromId: string) {
    const { contractorId: receiverContractorId } = messageFor as MessageFor;
    await sendMessage({
      variables: {
        input: {
          userProfileId: fromId,
          contractorId: receiverContractorId ?? "",
          message,
        },
      },
    });
  }

  async function onSubmitWithMessage() {
    const userId = await onSubmit(
      {
        email: signUpAndSendMessageFormValues.email,
        password: signUpAndSendMessageFormValues.password,
      },
      SegmentForms.SIGN_UP_AND_MESSAGE_PRO,
      false,
    );

    if (userId.length > 0) {
      await saveUserProfile(signUpAndSendMessageFormValues, userId);
    }
    if (signUpAndSendMessageFormValues.messageToPro?.length) {
      await startLeadConvertedConversations({
        variables: {
          input: {
            userId,
          },
        },
      });
      await sendMessageTo(signUpAndSendMessageFormValues.messageToPro, userId);
    }
    onSuccess && onSuccess();
    onClose();
    router.push("/messages");
  }

  if (updateUserProfileError) {
    console.error(updateUserProfileError);
  }

  if (sendMessageError) {
    console.error(sendMessageError);
  }

  return (
    <YzModal onClose={onClose} open={open}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography variant="h2" sx={loginModalTitle}>
          {title}
        </Typography>

        {subtitle && (
          <Typography sx={loginModalSubtitle}>{subtitle}</Typography>
        )}
        {showSignUpAndSendMessage ? (
          <SignUpAndSendMessageForm
            segmentFormName={SegmentForms.SIGN_UP_AND_MESSAGE_PRO}
            onSubmit={onSubmitWithMessage}
            submissionErrors={signupErrors}
            setSubmissionErrors={setSignupErrors}
            formValues={signUpAndSendMessageFormValues}
            setFormValues={setSignUpAndSendMessageFormValues}
            messageFor={messageFor}
            setTosChecked={setTosChecked}
          />
        ) : (
          <LoginForm
            buttonText="Get Started"
            buttonDisabled={false}
            showPassword
            flowName={SegmentFlows.SIGN_UP}
            showPasswordRequirements
            showSignupConsent
            submittingText="Creating Account"
            submissionErrors={signupErrors}
            setSubmissionErrors={setSignupErrors}
            onSubmit={onSubmit}
            isSignInForm={false}
            segmentFormName={segmentFormName}
            tosChecked={tosChecked}
            setTosChecked={setTosChecked}
          />
        )}

        <Box sx={{ marginTop: "1.5rem" }}>
          <Typography
            variant={"body_sm" as any}
            paragraph
            sx={{ textAlign: "center", marginBottom: "0.38rem" }}
          >
            Already have an account?{" "}
            <Link
              href={`/login?callbackUrl=${callbackUrl}`}
              style={{ color: "#323232" }}
              className="underline"
            >
              Log in
            </Link>
          </Typography>
          <Typography
            variant={"body_sm" as any}
            paragraph
            sx={{ textAlign: "center" }}
          >
            <Link
              href={`/forgot-password?callbackUrl=%${callbackUrl}`}
              style={{ color: "#323232" }}
              className="underline"
            >
              Forgot Password
            </Link>
          </Typography>
        </Box>
      </Box>
    </YzModal>
  );
}
